import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface AudioActionProps {
    actionValue: any[];
    heading?: string;
}

const KNOWN_MARKER_STYLES: Record<string, { bg: string; color: string }> = {
    SLOW:  { bg: '#e3f2fd', color: '#0d47a1' },
    FAST:  { bg: '#fce4ec', color: '#b71c1c' },
    QUIET: { bg: '#f3e5f5', color: '#4a148c' },
    LOUD:  { bg: '#fff3e0', color: '#e65100' },
};

function ScriptSegmentTokens({ segment }: { segment: string }): React.ReactElement {
    const parts: React.ReactNode[] = [];
    const tokenRegex = /<([A-Z]+)>|\(PAUSE\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = tokenRegex.exec(segment)) !== null) {
        const precedingText = segment.slice(lastIndex, match.index).trim();
        if (precedingText) {
            parts.push(
                <Typography
                    key={key++}
                    component="span"
                    sx={{ fontSize: '14px', fontFamily: 'Montserrat', mx: 0.5 }}
                >
                    {precedingText}
                </Typography>
            );
        }

        if (match[0] === '(PAUSE)') {
            parts.push(
                <Chip
                    key={key++}
                    label="PAUSE"
                    size="small"
                    sx={{
                        mx: 0.25,
                        fontSize: '10px',
                        height: 18,
                        bgcolor: '#f5f5f5',
                        color: '#616161',
                        border: '1px dashed #bdbdbd',
                    }}
                />
            );
        } else {
            const markerName = match[1];
            const style = KNOWN_MARKER_STYLES[markerName] ?? { bg: '#e8f5e9', color: '#1b5e20' };
            parts.push(
                <Chip
                    key={key++}
                    label={markerName}
                    size="small"
                    sx={{
                        mx: 0.25,
                        fontSize: '10px',
                        height: 18,
                        bgcolor: style.bg,
                        color: style.color,
                        fontWeight: 700,
                    }}
                />
            );
        }

        lastIndex = match.index + match[0].length;
    }

    const trailingText = segment.slice(lastIndex).trim();
    if (trailingText) {
        parts.push(
            <Typography
                key={key++}
                component="span"
                sx={{ fontSize: '14px', fontFamily: 'Montserrat', mx: 0.5 }}
            >
                {trailingText}
            </Typography>
        );
    }

    return <>{parts}</>;
}

function ScriptDisplay({ script, title }: { script: string; title: string }): React.ReactElement {
    const segments = script.split('|').map((s) => s.trim()).filter(Boolean);

    return (
        <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '14px', fontFamily: 'Montserrat', mb: 1 }}>
                {title} :
            </Typography>
            <Box
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {segments.map((segment, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                p: 1,
                                bgcolor: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                borderBottom: index < segments.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{ fontSize: '11px', fontWeight: 600, color: '#9e9e9e', mr: 1, minWidth: 20 }}
                            >
                                {index + 1}.
                            </Typography>
                            <ScriptSegmentTokens segment={segment} />
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default function AudioAction({ actionValue }: AudioActionProps) {
    if (!actionValue?.length) {
        return <Typography>No audio recordings available.</Typography>;
    }

    return (
        <>
            {actionValue.map((value: any, valueIndex: number) => {
                const audioMode: string = value.config_details?.value?.audio_mode ?? '';
                const scriptContent: string = value.config_details?.value?.script_source_content ?? '';
                const title: string = value.config_details?.value?.title ?? 'Audio';
                const isCompletedWithoutUpload: boolean = value.is_completed_without_audio_upload === true;

                if (audioMode === 'only_script') {
                    return (
                        <Box key={valueIndex} sx={{ mb: 2 }}>
                            {scriptContent && <ScriptDisplay script={scriptContent} title={title} />}
                            {isCompletedWithoutUpload ? (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 1,
                                        bgcolor: '#fff8e1',
                                        border: '1px solid #ffe082',
                                    }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: '#f57c00' }} />
                                    <Typography sx={{ fontSize: '13px', fontFamily: 'Montserrat', color: '#e65100' }}>
                                        Audio was recorded on the separate device
                                    </Typography>
                                </Box>
                            ) : (
                                value.url && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <audio controls>
                                            <source src={value.url} type="audio/mpeg" />
                                            Your browser does not support the audio element.
                                        </audio>
                                    </Box>
                                )
                            )}
                        </Box>
                    );
                }

                if (audioMode === 'script_and_recording') {
                    return (
                        <Box key={valueIndex} sx={{ mb: 2 }}>
                            {scriptContent && <ScriptDisplay script={scriptContent} title={title} />}
                            <Box sx={{ mt: 1.5 }}>
                                <audio controls>
                                    <source src={value.url} type="audio/mpeg" />
                                    Your browser does not support the audio element.
                                </audio>
                            </Box>
                        </Box>
                    );
                }

                // Fallback for audio_recording_only or unrecognised mode
                return (
                    <div key={valueIndex} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row', marginBottom: '10px' }}>
                        <Typography sx={{ flex: 1, fontWeight: 'bold', marginRight: '10px', marginTop: '15px' }}>
                            {title} :
                        </Typography>
                        <div style={{ flex: 2 }}>
                            <audio controls style={{ marginLeft: '3.4%' }}>
                                <source src={value.url} type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    </div>
                );
            })}
        </>
    );
}
